import React, { useState, useMemo, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DialogActions, DialogContent } from '@mui/material';
import { AppButton } from '../app/button/appButton';
import { AppControlledTextField } from '../app/input/appControlledTextField';
import { Project } from '../../models/project';
import { Tower } from '../../models/tower';
import { AppControlledCheckbox } from '../app/input/appControlledCheckbox';
import { AppControlledAutocomplete } from '../app/input/appControlledAutocomplete';
import { ROLES_ID, RolesNames } from '../../models/employee';
import {
  DEFAULT_PAGE_SIZE,
  NO_CLIENTS,
  NO_PROJECT_MANAGERS,
  NO_TOWERS,
  NO_BUDGET_OWNERS,
  PLEASE_TYPE,
  NUMERIC_ONLY,
} from '../../constants';
import {
  emailValidationRegex,
  numberValidationRegex,
} from '../../services/validatonService';
import { SearchOptions } from '../../models/reports';
import {
  useGetClientsQuery,
  useGetEmployeesQuery,
} from '../../store/employees/employeesService';
import { getOptions } from '../../services/employeeService';
import { useGetProjectsByProjectCodeQuery } from '../../store/projects/projectsService';
import { useGetTowersQuery } from '../../store/towers/towersService';


const style = {
  dialogActions: {
    p: 0,
    mt: 2,
  },
} as const;

interface Props {
  onSubmit(data: Partial<Project>): void;
  onCancel(): void;
  project?: Project | null;
  isLoading?: boolean;
}

enum SearchField {
  CLIENT = 'client',
  TOWER = 'tower',
  BUDGER_OWNER = 'budgetOwner',
  PROJECT_MANAGER = 'projectManager',
}

export function ProjectForm({
  onSubmit,
  onCancel,
  project,
  isLoading = false,
}: Props): JSX.Element {
  const defaultValueProjectOwner = useMemo(
    () =>
      project
        ? {
          label: project.projectOwner.name,
          id: project.projectOwner.id,
        }
        : null,
    [project]
  );
  const defaultValueClient = useMemo(
    () =>
      project
        ? {
          label: project.client.name,
          id: project.client.id,
        }
        : null,
    [project]
  );
  const defaultValueBudgetOwner = useMemo(
    () =>
      project
        ? {
          label: project.salesManager.name,
          id: project.salesManager.id,
        }
        : null,
    [project]
  );
  const defaultValueOngoing = useMemo(
    () => (project ? project.ongoing : true),
    [project]
  );
  const defaultIsAms = useMemo(
    () => (project ? project.isAMS : false),
    [project]
  );
  const defaultValueTower = useMemo(
    () =>
      project
        ? {
          label: project.tower.name,
          id: project.tower.id,
        }
        : null,
    [project]
  );
  const defaultValues = {
    code: project?.code,
    name: project?.name,
    transferIndex: project?.transferIndex || 80,
    client: defaultValueClient,
    projectOwner: defaultValueProjectOwner,
    budgetOwner: defaultValueBudgetOwner,
    ongoing: defaultValueOngoing,
    responsibleClientEmail: project?.responsibleClientEmail,
    responsibleClientName: project?.responsibleClientName,
    isAMS: defaultIsAms,
    tower: defaultValueTower,
  };

  const methods = useForm({ defaultValues });

  const { errors: formErrors } = methods.formState;

  const isDirty = methods.formState.isDirty;

  const [searchOptions, setSearchOptions] = useState<
    SearchOptions<SearchField>
  >({
    value: '',
  });

  const { data: clientsQueryData, isLoading: isLoadingClients } =
    useGetClientsQuery({
      take: DEFAULT_PAGE_SIZE,
      ...{
        filter:
          searchOptions.field === SearchField.CLIENT ? searchOptions.value : '',
      },
    });

  const { data: towersQueryData, isLoading: isLoadingTowers } = 
    useGetTowersQuery({
      take: DEFAULT_PAGE_SIZE,
      ...{
        filter:
          searchOptions.field === SearchField.TOWER ? searchOptions.value : '',
      },
    });

  const { data: budgetOwnersQueryData, isLoading: isLoadingBudgetOwners } =
    useGetEmployeesQuery({
      take: DEFAULT_PAGE_SIZE,
      includeInactive: true,
      roleId: ROLES_ID.SALES_MANAGER,
      ...{
        filter:
          searchOptions.field === SearchField.BUDGER_OWNER
            ? searchOptions.value
            : '',
      },
    });

  const {
    data: projectManagersQueryData,
    isLoading: isLoadingProjectManagers,
  } = useGetEmployeesQuery({
    take: DEFAULT_PAGE_SIZE,
    includeInactive: true,
    roleId: ROLES_ID.PROJECT_MANAGER,
    ...{
      filter:
        searchOptions.field === SearchField.PROJECT_MANAGER
          ? searchOptions.value
          : '',
    },
  });

  const code = methods.watch('code');
  const shouldFetchProjects = useMemo(() => code?.length === 5, [code]);
  const { data: projectsData, isFetching } = useGetProjectsByProjectCodeQuery(
    code,
    { skip: !shouldFetchProjects }
  );


  const projects = useMemo(
    () => (shouldFetchProjects ? projectsData || [] : []),
    [projectsData, shouldFetchProjects]
  );

  const projectManagersOptions = useMemo(() => {
    return projectManagersQueryData
      ? getOptions(projectManagersQueryData?.items)
      : [];
  }, [projectManagersQueryData]);

  const budgetOwnersOptions = useMemo(() => {
    return budgetOwnersQueryData
      ? getOptions(budgetOwnersQueryData?.items)
      : [];
  }, [budgetOwnersQueryData]);


  const towerOptions = useMemo(() => {
    return towersQueryData ? getOptions(towersQueryData?.items) : [];
  }, [towersQueryData]);

  const clientsOptions = useMemo(() => {
    return clientsQueryData ? getOptions(clientsQueryData?.items) : [];
  }, [clientsQueryData]);

  const searchProjectManagerOptions = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.PROJECT_MANAGER });
  }, []);

  const searchBudgetOwnerOptions = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.BUDGER_OWNER });
  }, []);

  const searchClientOptions = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.CLIENT });
  }, []);

  const searchTowersOptions = useCallback(
    (name: string) => {
      setSearchOptions({ value: name, field: SearchField.TOWER });
    },
    []
  );

  const submit = useCallback(
    (data) => {
      onSubmit(data);
    },
    [onSubmit]
  );

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(submit)}
        noValidate
        autoComplete="off"
      >
        <DialogContent>
          <AppControlledTextField
            label="Code"
            name="code"
            isRequired
            isError={!!formErrors.code}
            fullWidth
            maxLength={{ value: 5, message: 'Code must be 5 digits' }}
            minLength={{ value: 5, message: 'Code must be 5 digits' }}
            pattern={{
              value: numberValidationRegex,
              message: NUMERIC_ONLY,
            }}
            sx={{ mb: 3 }}
          />
          <AppControlledAutocomplete
            label="Name"
            name="name"
            isError={!!formErrors.name}
            rules={{
              validate: (value: string) => {
                const formatValue = (val: string) =>
                  val.toLocaleLowerCase().replace(/ /g, '');

                const isDefaultValue =
                  defaultValues.name &&
                  formatValue(value) === formatValue(defaultValues.name);

                if (isDefaultValue) return true;

                const isProjectNameUsed = projects.some(
                  (item) => formatValue(item.name) === formatValue(value)
                );

                return isProjectNameUsed ? 'Project name already exists' : true;
              },
            }}
            getOptionLabel={(option: string | Project) =>
              typeof option === 'string'
                ? option
                : `${option.name} (${option.projectOwner.name})`
            }
            isOptionEqualToValue={(option, value) => option.name === value.name}
            errorMessage={formErrors.name?.message}
            getOptionDisabled={() => true}
            options={projects}
            isFreeSolo
            autoSelect
            sx={{ mb: 3 }}
            placeholder={PLEASE_TYPE}
            groupBy={() => 'Existing projects'}
            isRequired
            isLoading={isFetching}
          />

          <AppControlledAutocomplete
            label="Client"
            name="client"
            options={clientsOptions}
            searchOptions={searchClientOptions}
            sx={{ mb: 3 }}
            isError={!!formErrors.client}
            isLoading={isLoadingClients}
            noOptionsText={NO_CLIENTS}
            placeholder={PLEASE_TYPE}
            isRequired
          />
          <AppControlledAutocomplete
            label={RolesNames.PROJECT_MANAGER}
            name="projectOwner"
            options={projectManagersOptions}
            searchOptions={searchProjectManagerOptions}
            sx={{ mb: 3 }}
            isError={!!formErrors.projectOwner}
            isLoading={isLoadingProjectManagers}
            noOptionsText={NO_PROJECT_MANAGERS}
            placeholder={PLEASE_TYPE}
            isRequired
          />

          <AppControlledAutocomplete
            label={RolesNames.SALES_MANAGER}
            name="budgetOwner"
            options={budgetOwnersOptions}
            searchOptions={searchBudgetOwnerOptions}
            sx={{ mb: 3 }}
            isError={!!formErrors.budgetOwner}
            isLoading={isLoadingBudgetOwners}
            noOptionsText={NO_BUDGET_OWNERS}
            placeholder={PLEASE_TYPE}
            isRequired
          />

          <AppControlledTextField
            label="Client Responsible Name"
            name="responsibleClientName"
            isError={!!formErrors.responsibleClientName}
            sx={{ mb: 3 }}
            fullWidth
            maxLength={100}
          />

          <AppControlledTextField
            label="Client Responsible Email"
            name="responsibleClientEmail"
            isError={!!formErrors.responsibleClientEmail}
            sx={{ mb: 3 }}
            fullWidth
            maxLength={100}
            pattern={{
              value: emailValidationRegex,
              message: 'Please enter a valid email address.',
            }}
            helperText={formErrors.responsibleClientEmail?.message}
            placeholder={PLEASE_TYPE}
          />

          <AppControlledAutocomplete
            label="Tower"
            name="tower"
            options={towerOptions}
            searchOptions={searchTowersOptions}
            sx={{ mb: 3 }}
            isError={!!formErrors.tower}
            isLoading={isLoadingTowers}
            noOptionsText={NO_TOWERS}
            placeholder={PLEASE_TYPE}
            isRequired
          />

          <AppControlledTextField
            label="Transfer Index %"
            name="transferIndex"
            isRequired
            isError={!!formErrors.transferIndex}
            fullWidth
            minValue={0}
            maxValue={100}
            pattern={{
              value: numberValidationRegex,
              message: NUMERIC_ONLY,
            }}
            rules={{
              min: {
                value: 0,
                message: 'Transfer index must be greater than or equal to 0',
              },
              max: {
                value: 100,
                message: 'Transfer index must be less than or equal to 100',
              },
            }}
            sx={{ mb: 3 }}
          />

          <AppControlledCheckbox
            label="Apply AMS settings"
            name="isAMS"
            labelSx={{ marginTop: 1 }}
            defaultValue={defaultIsAms}
          />
          <br />
          <AppControlledCheckbox
            label="Active"
            name="ongoing"
            defaultValue={defaultValueOngoing}
          />
        </DialogContent>
        <DialogActions sx={style.dialogActions}>
          <AppButton label="Cancel" onClick={onCancel} />
          <AppButton
            label="Save"
            isPrimary
            buttonType="submit"
            isLoading={isLoading}
            disabled={!isDirty}
          />
        </DialogActions>
      </form>
    </FormProvider>
  );
}
